import { useState, useEffect, useCallback } from 'react';
import { sleepDB, initializeBackupSystem } from '../database/db';
import { getSleepStats, groupEntriesByDate } from '../utils/dateUtils';

export const useSleepData = (selectedDate, childId) => {
  const [entries, setEntries] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize backup system
  useEffect(() => {
    initializeBackupSystem();
  }, []);

  // Load children
  const loadChildren = useCallback(async () => {
    setChildrenLoading(true);
    try {
      const childrenData = await sleepDB.getChildren();
      setChildren(childrenData);
      
      // Auto-select first child if none selected
      if (childrenData.length > 0 && !selectedChildId) {
        setSelectedChildId(childrenData[0].id);
      }
    } catch (err) {
      setError('Fehler beim Laden der Kinder-Daten');
      console.error('Error loading children:', err);
    } finally {
      setChildrenLoading(false);
    }
  }, [selectedChildId]);

  // Load entries when date or child changes
  useEffect(() => {
    if (selectedDate && childId) {
      loadEntries();
    } else {
      setEntries([]);
    }
  }, [selectedDate, childId]);

  const loadEntries = async () => {
    if (!selectedDate || !childId) return;

    setEntriesLoading(true);
    setError(null);

    try {
      const data = await sleepDB.getEntriesByDate(selectedDate, childId);
      setEntries(data);
    } catch (err) {
      console.error('Error loading entries:', err);
      setError('Fehler beim Laden der Einträge');
    } finally {
      setEntriesLoading(false);
    }
  };

  // Add new child
  const addChild = useCallback(async (childData) => {
    setChildrenLoading(true);
    setError(null);
    
    try {
      const childId = await sleepDB.addChild(childData);
      await loadChildren();
      
      // Auto-select new child if it's the first one
      if (children.length === 0) {
        setSelectedChildId(childId);
      }
      
      return childId;
    } catch (err) {
      setError('Fehler beim Hinzufügen des Kindes');
      console.error('Error adding child:', err);
      throw err;
    } finally {
      setChildrenLoading(false);
    }
  }, [children.length, loadChildren]);

  // Update child
  const updateChild = useCallback(async (childId, updates) => {
    setChildrenLoading(true);
    setError(null);
    
    try {
      await sleepDB.updateChild(childId, updates);
      await loadChildren();
    } catch (err) {
      setError('Fehler beim Aktualisieren des Kindes');
      console.error('Error updating child:', err);
      throw err;
    } finally {
      setChildrenLoading(false);
    }
  }, [loadChildren]);

  // Delete child
  const deleteChild = useCallback(async (childId) => {
    setChildrenLoading(true);
    setError(null);
    
    try {
      await sleepDB.deleteChild(childId);
      await loadChildren();
      
      // If deleted child was selected, select first available child
      if (selectedChildId === childId) {
        const remainingChildren = await sleepDB.getChildren();
        if (remainingChildren.length > 0) {
          setSelectedChildId(remainingChildren[0].id);
        } else {
          setSelectedChildId(null);
        }
      }
    } catch (err) {
      setError('Fehler beim Löschen des Kindes');
      console.error('Error deleting child:', err);
      throw err;
    } finally {
      setChildrenLoading(false);
    }
  }, [selectedChildId, loadChildren]);

  // Add new entry
  const addEntry = async (entryData) => {
    try {
      const id = await sleepDB.addEntry(entryData);
      await loadEntries(); // Reload entries
      return id;
    } catch (err) {
      console.error('Error adding entry:', err);
      throw new Error('Fehler beim Hinzufügen des Eintrags');
    }
  };

  // Update entry
  const updateEntry = async (entryId, entryData) => {
    try {
      await sleepDB.updateEntry(entryId, entryData);
      await loadEntries(); // Reload entries
    } catch (err) {
      console.error('Error updating entry:', err);
      throw new Error('Fehler beim Aktualisieren des Eintrags');
    }
  };

  // Delete entry
  const deleteEntry = async (entryId) => {
    try {
      await sleepDB.deleteEntry(entryId);
      await loadEntries(); // Reload entries
    } catch (err) {
      console.error('Error deleting entry:', err);
      throw new Error('Fehler beim Löschen des Eintrags');
    }
  };

  // Get statistics
  const getStats = useCallback(() => {
    return getSleepStats(entries);
  }, [entries]);

  // Get grouped entries
  const getGroupedEntries = useCallback(() => {
    return groupEntriesByDate(entries);
  }, [entries]);

  // Get selected child
  const getSelectedChild = useCallback(() => {
    return children.find(child => child.id === selectedChildId);
  }, [children, selectedChildId]);

  // Backup operations
  const createBackup = useCallback(async () => {
    try {
      return await sleepDB.createBackup();
    } catch (err) {
      setError('Fehler beim Erstellen des Backups');
      throw err;
    }
  }, []);

  const restoreBackup = useCallback(async () => {
    try {
      const success = await sleepDB.restoreBackup();
      if (success) {
        await loadChildren();
        if (selectedChildId) {
          await loadEntries();
        }
      }
      return success;
    } catch (err) {
      setError('Fehler beim Wiederherstellen des Backups');
      throw err;
    }
  }, [loadChildren, selectedChildId, loadEntries]);

  const exportData = useCallback(async () => {
    try {
      return await sleepDB.exportData();
    } catch (err) {
      setError('Fehler beim Exportieren der Daten');
      throw err;
    }
  }, []);

  const importData = useCallback(async (data) => {
    try {
      await sleepDB.importData(data);
      await loadChildren();
      return true;
    } catch (err) {
      setError('Fehler beim Importieren der Daten');
      throw err;
    }
  }, [loadChildren]);

  // Load initial data
  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const refreshEntries = async () => {
    await loadEntries();
  };

  return {
    // Data
    entries,
    children,
    selectedChildId,
    selectedChild: getSelectedChild(),
    entriesLoading,
    childrenLoading,
    error,
    selectedDate,
    
    // Actions
    setSelectedChildId,
    addChild,
    updateChild,
    deleteChild,
    addEntry,
    updateEntry,
    deleteEntry,
    loadEntries,
    getStats,
    getGroupedEntries,
    
    // Backup operations
    createBackup,
    restoreBackup,
    exportData,
    importData,
    refreshEntries
  };
}; 